import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useExtraWorkOrderDetail, useStartLine } from '../../../List/hooks';
import { LoadingState, ErrorState } from '../feedback';
import {
  OrderHeader,
  Breadcrumb,
  CartScanner,
  GroupsList,
  ActiveCartItem,
  GroupScanner,
  GroupDetail,
  QuantityControls,
  WorkOrderFinishedMessage,
} from '../display';
import { RegisterWasteModal, CompleteLineModal } from '../../../List/components/modals';
import { useExtraWorkDispatch } from '../../hooks';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import { Product } from '@shared/components/ui/Product';
import type { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag, faClock, faBoxesStacked } from '@fortawesome/pro-light-svg-icons';
import { format } from 'date-fns';

/**
 * Componente principal del detalle de orden de trabajo extra
 */
export const Main = () => {
  const { t } = useTranslation('extraWorkOrdersModule');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const orderId = id ? parseInt(id, 10) : null;

  // Refs para modales
  const registerWasteModalRef = useRef<ModalCenterRef>(null);
  const completeLineModalRef = useRef<ModalCenterRef>(null);
  const confirmDispatchModalRef = useRef<ConfirmModalRef>(null);

  // Estado para línea seleccionada
  const [selectedLineId] = useState<number | null>(null);
  // Estado para carrito activo
  const [activeCart, setActiveCart] = useState<{ code: string; data: any } | null>(null);
  // Estado para grupo activo
  const [activeGroup, setActiveGroup] = useState<{ code: string; data: any } | null>(null);

  // Hook de datos
  const { order, isLoading, error, refetch } = useExtraWorkOrderDetail(orderId);
  const { dispatchCart, isDispatching } = useExtraWorkDispatch(orderId);
  const startLineMutation = useStartLine();

  // Handlers
  const handleStartLine = async (lineId: number) => {
    if (!orderId) return;

    try {
      await startLineMutation.mutateAsync({
        orderId,
        lineId,
      });
      await refetch();
    } catch (err) {
      // Error handled by mutation or global handler
    }
  };

  const handleWasteRegistered = () => {
    refetch();
  };

  const handleLineCompleted = () => {
    refetch();
  };

  const confirmDispatch = useCallback(
    (locationId: number) => {
      if (!orderId || !order) return;

      // Iniciamos la llamada en segundo plano para que la modal se cierre de inmediato
      dispatchCart({
        location_id: locationId,
      })
        .then(() => {
          refetch();
        })
        .catch((_err) => {
          // Error handled by feedback UI or mutation
        });
    },
    [orderId, order, dispatchCart, refetch],
  );

  const handleDispatchLine = useCallback(
    (locationId: number) => {
      if (!confirmDispatchModalRef.current) return;

      const linesToDispatch = order?.lines?.filter((l) => l.done_location_id === locationId) || [];

      confirmDispatchModalRef.current.open(
        () => confirmDispatch(locationId),
        undefined,
        t('actions.dispatch', 'Despachar'),
        t('detail.quantity.dispatch.confirm_message', '¿Estás seguro de que quieres despachar este carrito?'),
        undefined,
        undefined,
        () => (
          <div className="mt-4 space-y-3">
            <p className="text-xs font-semibold text-complementary-text uppercase tracking-wider">
              {t('detail.dispatch.products_to_send', 'Productos a enviar')} ({linesToDispatch.length})
            </p>
            <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
              {linesToDispatch.map((line) => (
                <div
                  key={line.id}
                  className="flex items-center justify-between p-2 rounded-lg border border-card-border bg-card-bg/50"
                >
                  <div className="flex-1 min-w-0">
                    <Product
                      id={line.product?.id || line.product_id || 0}
                      name={line.product?.name || 'Producto'}
                      code={line.product?.code || String(line.product_id || '')}
                      src={line.product?.image_hash || ''}
                      enableLineClamp={true}
                    />
                  </div>
                  <div className="ml-4 flex-shrink-0 text-sm font-bold text-title">
                    {line.qty_done} / {line.qty_expected}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ),
      );
    },
    [confirmDispatch, t, order?.lines],
  );

  // Estados de UI
  if (isLoading) {
    return <LoadingState message={t('detail.loading', 'Cargando orden...')} />;
  }

  if (error || !order || orderId === null) {
    return (
      <ErrorState error={error || new Error('Orden no encontrada')} onRetry={() => navigate('/extra-work-orders')} />
    );
  }

  const selectedLine = order.lines?.find((l) => l.id === selectedLineId);
  const allLinesCompleted =
    order.lines && order.lines.length > 0 && order.lines.every((line) => line.status === 'DONE');

  return (
    <div className="px-6 py-4 space-y-6">
      <Breadcrumb order={order} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div
          className={`flex-shrink-0 space-y-4 ${allLinesCompleted ? 'lg:min-h-[calc(100vh-140px)] lg:max-h-[calc(100vh-140px)]' : ''}`}
        >
          {allLinesCompleted ? (
            <WorkOrderFinishedMessage />
          ) : activeCart ? (
            <>
              <ActiveCartItem
                cartCode={activeCart.code}
                onChange={() => {
                  setActiveCart(null);
                  setActiveGroup(null);
                }}
              />
              {!activeGroup && (
                <GroupScanner
                  orderLines={order.lines || []}
                  orderId={orderId}
                  onStartLine={handleStartLine}
                  onGroupValidated={async (code, data) => {
                    const { data: updatedOrder } = await refetch();
                    const updatedLine = updatedOrder?.lines?.find((l: any) => l.id === data.id) || data;
                    setActiveGroup({ code, data: updatedLine });
                  }}
                />
              )}
              {activeGroup && (
                <>
                  <div className="bg-green-50 dark:bg-green-950/20 border border-border rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-green-100 dark:bg-green-900/40 rounded-lg p-2 flex-shrink-0">
                        <FontAwesomeIcon icon={faBoxesStacked} className="w-5 h-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] text-success font-medium mb-0.5">
                          {t('detail.group.title', 'Grupo Escaneado')}
                        </p>
                        <p className="text-sm font-bold text-title truncate">{activeGroup.data.group_code}</p>
                      </div>
                      <div className="flex flex-col gap-2 text-xs flex-shrink-0 items-end">
                        <div className="flex items-center gap-2 justify-end">
                          <FontAwesomeIcon icon={faTag} className="w-3 h-3 text-icons" />
                          <span className="text-text text-right">
                            <span className="text-complementary-text">
                              {t('detail.lines.work_type', 'Tipo trabajo')}:
                            </span>{' '}
                            <span className="font-medium">
                              {t(`workTypes.${activeGroup.data.work_type}`, activeGroup.data.work_type) as string}
                            </span>
                          </span>
                        </div>
                        {activeGroup.data.started_at && (
                          <div className="flex items-center gap-2 justify-end">
                            <FontAwesomeIcon icon={faClock} className="w-3 h-3 text-icons" />
                            <span className="text-text font-medium text-right">
                              {format(new Date(activeGroup.data.started_at), 'dd/MM/yyyy HH:mm')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <QuantityControls
                    orderId={orderId}
                    line={activeGroup.data}
                    activeCartCode={activeCart?.code}
                    onSuccess={() => {
                      setActiveCart(null);
                      setActiveGroup(null);
                      refetch();
                    }}
                    onReset={() => {
                      setActiveCart(null);
                      setActiveGroup(null);
                      refetch();
                    }}
                  />
                </>
              )}
            </>
          ) : (
            <CartScanner onCartValidated={(code, data) => setActiveCart({ code, data })} />
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-6">
          <OrderHeader order={order} />
          {activeGroup ? (
            <GroupDetail line={activeGroup.data} order={order} />
          ) : (
            <GroupsList
              lines={order.lines || []}
              order={order}
              onDispatch={handleDispatchLine}
              isDispatching={isDispatching}
              onLineClick={(_lId) => {}}
            />
          )}
        </div>
      </div>
      {selectedLineId && orderId && (
        <>
          <RegisterWasteModal
            ref={registerWasteModalRef}
            orderId={orderId}
            lineId={selectedLineId}
            onSuccess={handleWasteRegistered}
          />
          {selectedLine && (
            <CompleteLineModal
              ref={completeLineModalRef}
              orderId={orderId}
              line={selectedLine}
              onSuccess={handleLineCompleted}
            />
          )}
        </>
      )}
      <ConfirmModal ref={confirmDispatchModalRef} />
    </div>
  );
};
